import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createUser, importUsers, listUsers, type ImportResult } from "@/api/users";
import { Button, Card, Input, Skeleton } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { toast } from "@/state/toast.store";
import { useLang } from "@/i18n/LanguageContext";
import { ManageableUserRow } from "@/features/users/ManageableUserRow";

const EMPTY = {
  email: "",
  password: "",
  role: "employee" as "org_admin" | "manager" | "employee",
  full_name: "",
  job_title: "",
  department: "",
  location: "",
};

export function UsersPanel({ canCreate }: { canCreate: boolean }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const actorRole = useAuthStore((s) => s.user?.role) ?? "employee";
  const users = useQuery({ queryKey: ["users"], queryFn: ({ signal }) => listUsers(signal) });
  const [form, setForm] = useState(EMPTY);
  const [invite, setInvite] = useState(true);
  const [query, setQuery] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState("email,full_name,role,job_title,department\n");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const doImport = useMutation({
    mutationFn: () => importUsers(csvText),
    onSuccess: (res) => {
      setImportResult(res);
      toast.ok(t(`${res.created} medewerker(s) uitgenodigd.`, `${res.created} person(s) invited.`));
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const create = useMutation({
    mutationFn: () =>
      createUser({
        email: form.email,
        role: form.role,
        full_name: form.full_name || null,
        invite,
        password: invite ? undefined : form.password,
        profile:
          form.role === "employee" || form.role === "manager"
            ? { job_title: form.job_title, department: form.department, location: form.location }
            : null,
      }),
    onSuccess: () => {
      setForm(EMPTY);
      toast.ok(invite ? t("Uitnodiging verstuurd.", "Invitation sent.") : t("Medewerker aangemaakt.", "Person created."));
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  const refetch = () => qc.invalidateQueries({ queryKey: ["users"] });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !users.data) return users.data ?? [];
    return users.data.filter(
      (u) => u.email.toLowerCase().includes(q) || (u.full_name ?? "").toLowerCase().includes(q),
    );
  }, [users.data, query]);

  return (
    <div className="cards" style={{ gridTemplateColumns: canCreate ? "1.2fr 1fr" : "1fr" }}>
      <Card>
        <p className="card-num">{t("MEDEWERKERS", "PEOPLE")}</p>
        <h3>{t("Accounts in deze organisatie", "Accounts in this organisation")}</h3>
        {users.data && users.data.length > 0 && (
          <Input
            name="user-search"
            placeholder={t("Zoek op naam of e-mail…", "Search by name or e-mail…")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
        {users.isLoading ? (
          <Skeleton lines={4} />
        ) : filtered.length > 0 ? (
          <ul className="list-rows">
            {filtered.map((u) => (
              <ManageableUserRow key={u.id} user={u} actorRole={actorRole} onChanged={refetch} />
            ))}
          </ul>
        ) : (
          <p className="no-data-message">
            {users.data && users.data.length > 0
              ? t("Geen resultaten.", "No matches.")
              : t("Nog geen medewerkers.", "No people yet.")}
          </p>
        )}
      </Card>

      {canCreate && (
        <Card>
          <p className="card-num">{t("NIEUW", "NEW")}</p>
          <h3>{t("Medewerker toevoegen", "Add a person")}</h3>
          <form onSubmit={onSubmit}>
            <Input label={t("Naam", "Name")} name="full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            <Input label={t("E-mail", "Email")} name="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <label className="ack-check" style={{ fontWeight: 500, fontSize: 14 }}>
              <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
              {t("Uitnodiging per e-mail sturen (gebruiker kiest eigen wachtwoord)", "Send an e-mail invitation (the user picks their own password)")}
            </label>
            {!invite && (
              <Input label={t("Wachtwoord", "Password")} name="password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            )}
            <div className="field">
              <label htmlFor="role">{t("Rol", "Role")}</label>
              <select id="role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as typeof form.role })}>
                <option value="employee">{t("Medewerker", "Employee")}</option>
                <option value="manager">{t("Manager", "Manager")}</option>
                <option value="org_admin">{t("Org-beheerder", "Org admin")}</option>
              </select>
            </div>
            {(form.role === "employee" || form.role === "manager") && (
              <>
                <Input label={t("Functie", "Job title")} name="job_title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
                <Input label={t("Afdeling", "Department")} name="department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
                <Input label={t("Locatie", "Location")} name="location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </>
            )}
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? t("Bezig…", "Working…") : invite ? t("Uitnodiging versturen", "Send invitation") : t("Medewerker aanmaken", "Create person")}
            </Button>
          </form>

          <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
            <button type="button" className="eyebrow" style={{ background: "none", border: 0, cursor: "pointer", padding: 0 }} onClick={() => setShowImport((v) => !v)}>
              {showImport ? t("− Importeren via CSV", "− Import via CSV") : t("+ Importeren via CSV", "+ Import via CSV")}
            </button>
            {showImport && (
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 12.5, color: "var(--grey-text)", marginBottom: 6 }}>
                  {t("Kopregel met minimaal 'email'. Kolommen: email, full_name, role, job_title, department, team, location, start_date, language. Iedereen krijgt een uitnodiging.",
                    "Header row with at least 'email'. Columns: email, full_name, role, job_title, department, team, location, start_date, language. Everyone gets an invitation.")}
                </p>
                <textarea className="body" style={{ minHeight: 120, fontSize: 13 }} value={csvText} onChange={(e) => setCsvText(e.target.value)} />
                <Button variant="outline" style={{ marginTop: 8 }} disabled={doImport.isPending} onClick={() => doImport.mutate()}>
                  {doImport.isPending ? t("Bezig…", "Working…") : t("Importeren", "Import")}
                </Button>
                {importResult && (
                  <div style={{ marginTop: 10, fontSize: 13.5 }}>
                    <p>{t("Aangemaakt", "Created")}: <strong>{importResult.created}</strong></p>
                    {importResult.failed.length > 0 && (
                      <ul style={{ color: "#8a2b2b", marginTop: 4 }}>
                        {importResult.failed.map((f, i) => <li key={i}>{f.email || "—"}: {f.error}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
